import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCopy, IconDeviceFloppy } from "@tabler/icons-react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  ContractSidecostGroup,
  ContractSidecostGroupsResponse,
  copyContractSidecostGroups,
  getContractSidecostGroups,
  saveContractSidecostGroups,
} from "../../../services/ContractSidecostGroupsService";

interface ContractSidecostGroupsPageProps {
  onBack?: () => void;
  initialData?: ContractSidecostGroupsResponse | null;
  onDataLoaded?: (data: ContractSidecostGroupsResponse) => void;
}

function hasSelectedItems(groups: ContractSidecostGroup[]): boolean {
  return groups.some((group) => group.items.some((item) => item.selected));
}

function normalizeNumberInputValue(value: string | number): number | null {
  if (typeof value === "number") {
    return Number.isNaN(value) ? null : value;
  }

  if (value === "") {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
}

export default function ContractSidecostGroupsPage({
  onBack: _onBack,
  initialData,
  onDataLoaded,
}: Readonly<ContractSidecostGroupsPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 708,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();
  const canManage = checkAccess("SIVUKULUT_YLLAPITO");

  const [data, setData] = useState<ContractSidecostGroupsResponse | null>(
    initialData ?? null,
  );
  const [loading, setLoading] = useState(!initialData);
  const [saving, setSaving] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    initialData?.selectedContractId ?? null,
  );
  const [copySourceContractId, setCopySourceContractId] = useState<
    string | null
  >(null);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<number>>(
    () => new Set(),
  );

  const showError = useCallback(
    (fallback: string, error: unknown) => {
      console.error(error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : fallback);

      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message,
        color: "red",
      });
    },
    [getTranslation],
  );

  const loadData = useCallback(
    async (contractId?: number | null) => {
      if (selectedAddressSystemId <= 0) {
        const emptyData: ContractSidecostGroupsResponse = {
          hideWorkItemNumber: true,
          useWorkItemCode: false,
          showWorkItemAreaPerPriceItem: false,
          selectedContractId: null,
          hasAnySidecostItems: false,
          availableContracts: [],
          groups: [],
        };
        setData(emptyData);
        onDataLoaded?.(emptyData);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await getContractSidecostGroups(
          selectedAddressSystemId,
          contractId,
        );
        setData(result);
        setSelectedContractId(result.selectedContractId ?? null);
        if (
          result.selectedContractId &&
          copySourceContractId === String(result.selectedContractId)
        ) {
          setCopySourceContractId(null);
        }
        onDataLoaded?.(result);
      } catch (error) {
        showError(
          getTranslation("MESSAGE_LOAD_FAILED") ||
            "Failed to load contract indirect costs",
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [
      copySourceContractId,
      getTranslation,
      onDataLoaded,
      selectedAddressSystemId,
      showError,
    ],
  );

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setSelectedContractId(initialData.selectedContractId ?? null);
      setLoading(false);
      return;
    }

    void loadData();
  }, [initialData, loadData]);

  const updateGroup = (
    groupId: number,
    updater: (group: ContractSidecostGroup) => ContractSidecostGroup,
  ) => {
    setData((current) => {
      if (!current) return current;
      const next = {
        ...current,
        groups: current.groups.map((group) =>
          group.id === groupId ? updater(group) : group,
        ),
      };
      next.hasAnySidecostItems = hasSelectedItems(next.groups);
      onDataLoaded?.(next);
      return next;
    });
  };

  const updateItem = (
    groupId: number,
    generalSidecostItemId: number,
    field: "selected" | "showAltValueField" | "unitPrice",
    value: boolean | number | null,
  ) => {
    updateGroup(groupId, (group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.generalSidecostItemId !== generalSidecostItemId) {
          return item;
        }

        if (field === "selected") {
          const selected = Boolean(value);
          return {
            ...item,
            selected,
            showAltValueField: selected ? item.showAltValueField : false,
          };
        }

        if (field === "showAltValueField") {
          return { ...item, showAltValueField: Boolean(value) };
        }

        return { ...item, unitPrice: typeof value === "number" ? value : null };
      }),
    }));
  };

  const handleContractChange = async (contractId: number | null) => {
    setSelectedContractId(contractId);
    setCopySourceContractId(null);
    await loadData(contractId);
  };

  const handleSave = async () => {
    if (!data || !selectedContractId) return;

    setSaving(true);
    try {
      await saveContractSidecostGroups(selectedAddressSystemId, selectedContractId, {
        items: data.groups.flatMap((group) =>
          group.items.map((item) => ({
            generalSidecostItemId: item.generalSidecostItemId,
            unitPrice: item.unitPrice ?? null,
            selected: item.selected,
            showAltValueField: item.showAltValueField,
          })),
        ),
      });

      notifications.show({
        title: getTranslation("TEXT_BUTTON_TALLENNA") || "Save",
        message: getTranslation("TEXT_SAVE_SUCCESS") || "Saved successfully",
        color: "green",
      });
      await loadData(selectedContractId);
    } catch (error) {
      showError(
        getTranslation("MESSAGE_SAVE_FAILED") ||
          "Failed to save contract indirect costs",
        error,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedContractId || !copySourceContractId) return;

    setSaving(true);
    try {
      await copyContractSidecostGroups(
        selectedAddressSystemId,
        selectedContractId,
        Number(copySourceContractId),
      );

      notifications.show({
        title: getTranslation("TEXT_BUTTON_KOPIOI") || "Copy",
        message: getTranslation("TEXT_SAVE_SUCCESS") || "Saved successfully",
        color: "green",
      });
      await loadData(selectedContractId);
    } catch (error) {
      showError(
        getTranslation("MESSAGE_COPY_FAILED") ||
          "Failed to copy contract indirect costs",
        error,
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleGroupCollapsed = (groupId: number) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const collapseAllGroups = () => {
    setCollapsedGroupIds(new Set(data?.groups.map((group) => group.id) ?? []));
  };

  const expandAllGroups = () => {
    setCollapsedGroupIds(new Set());
  };

  const headerCellStyle = {
    position: "sticky" as const,
    top: 0,
    zIndex: 2,
    background: "var(--mantine-color-gray-3)",
    color: "var(--mantine-color-dark-8)",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.03em",
  };
  const groupRowBackground = "var(--mantine-color-gray-1)";

  if (!canManage) {
    return (
      <Paper withBorder p="md">
        <Text c="dimmed">
          {getTranslation("TEXT_NO_RIGHTS") ||
            "You do not have access to this view."}
        </Text>
      </Paper>
    );
  }

  if (loading || !data) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  const contractOptions = data.availableContracts.map((contract) => ({
    value: String(contract.id),
    label: contract.name,
  }));
  const copyOptions = data.availableContracts
    .filter((contract) => contract.id !== selectedContractId)
    .map((contract) => ({
      value: String(contract.id),
      label: contract.name,
    }));
  const selectedContractValue =
    selectedContractId === null ? null : String(selectedContractId);

  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="md" h="100%" style={{ overflow: "hidden" }}>
        <Group justify="space-between" align="flex-end">
          <Select
            label={
              getTranslation("TEXT_LABEL_VALITSE_URAKKA") || "Select contract"
            }
            placeholder={
              getTranslation("TEXT_LABEL_VALITSE_URAKKA") || "Select contract"
            }
            data={contractOptions}
            value={selectedContractValue}
            onChange={(contractId) =>
              void handleContractChange(contractId ? Number(contractId) : null)
            }
            searchable
            clearable
            disabled={contractOptions.length === 0}
            w={420}
          />
          <Button
            type="button"
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={() => void handleSave()}
            loading={saving}
            disabled={!selectedContractId}
          >
            {getTranslation("TEXT_BUTTON_TALLENNA") || "Save"}
          </Button>
        </Group>

        {selectedContractId ? (
          <>
            {!data.hasAnySidecostItems && (
              <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                  <Text fw={600}>
                    {getTranslation("TEXT_COPY_EXISTING_UNIT_PRICE_LIST") ||
                      "Copy existing unit price list"}
                  </Text>
                  <Group align="flex-end">
                    <Select
                      label={
                        getTranslation("TEXT_LABEL_VALITSE_URAKKA") ||
                        "Select contract"
                      }
                      placeholder={
                        getTranslation("TEXT_LABEL_VALITSE_URAKKA") ||
                        "Select contract"
                      }
                      data={copyOptions}
                      value={copySourceContractId}
                      onChange={setCopySourceContractId}
                      searchable
                      w={420}
                    />
                    <Button
                      type="button"
                      leftSection={<IconCopy size={16} />}
                      onClick={() => void handleCopy()}
                      disabled={!copySourceContractId}
                      loading={saving}
                    >
                      {getTranslation("TEXT_BUTTON_KOPIOI") || "Copy"}
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            )}

            <Group justify="flex-end" gap="xs">
              <Button
                type="button"
                size="xs"
                variant="light"
                onClick={expandAllGroups}
              >
                {getTranslation("SETTINGS_EXPAND_ALL") || "Expand all"}
              </Button>
              <Button
                type="button"
                size="xs"
                variant="light"
                onClick={collapseAllGroups}
              >
                {getTranslation("SETTINGS_COLLAPSE_ALL") || "Collapse all"}
              </Button>
            </Group>

            {data.groups.length === 0 ? (
              <Text c="dimmed">
                {getTranslation("TEXT_NO_ROWS") ||
                  "No indirect cost groups available."}
              </Text>
            ) : (
              <Paper
                withBorder
                radius="md"
                style={{ flex: 1, minHeight: 0, overflow: "auto" }}
              >
                <Table
                  striped
                  highlightOnHover
                  withColumnBorders
                  horizontalSpacing="sm"
                  verticalSpacing="xs"
                  style={{ tableLayout: "fixed", minWidth: 980 }}
                >
                  <Table.Thead
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 4,
                    }}
                  >
                    <Table.Tr bg="var(--mantine-color-gray-3)">
                      {!data.hideWorkItemNumber && (
                        <Table.Th style={{ ...headerCellStyle, width: 110 }}>
                          {getTranslation("TEXT_LABEL_TVNRO") || "Item no."}
                        </Table.Th>
                      )}
                      <Table.Th style={{ ...headerCellStyle, width: 250 }}>
                        {getTranslation("TEXT_LABEL_LAJI") || "Type"}
                      </Table.Th>
                      <Table.Th style={{ ...headerCellStyle, width: 130 }}>
                        {getTranslation("TEXT_LABEL_YKSIKKO") || "Unit"}
                      </Table.Th>
                      <Table.Th style={{ ...headerCellStyle, width: 150 }}>
                        {getTranslation("TEXT_LABEL_YKSIKKOHINTA") ||
                          "Unit price"}
                      </Table.Th>
                      <Table.Th style={{ ...headerCellStyle, width: 110 }}>
                        {getTranslation("TEXT_LABEL_VALITTU") || "Selected"}
                      </Table.Th>
                      {data.showWorkItemAreaPerPriceItem && (
                        <Table.Th style={{ ...headerCellStyle, width: 140 }}>
                          {getTranslation("TEXT_LABEL_NAYTA_PINTAALA") ||
                            "Show area"}
                        </Table.Th>
                      )}
                    </Table.Tr>
                  </Table.Thead>

                  <Table.Tbody>
                    {data.groups.map((group) => {
                      const groupCollapsed = collapsedGroupIds.has(group.id);
                      return (
                        <Fragment key={group.id}>
                          <Table.Tr
                            bg={groupRowBackground}
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleGroupCollapsed(group.id)}
                          >
                            <Table.Td
                              colSpan={
                                4 +
                                (data.hideWorkItemNumber ? 0 : 1) +
                                (data.showWorkItemAreaPerPriceItem ? 1 : 0)
                              }
                            >
                              <Text fw={600}>{group.name}</Text>
                            </Table.Td>
                          </Table.Tr>

                          {!groupCollapsed &&
                            group.items.map((item) => {
                              const itemNumberLabel = data.useWorkItemCode
                                ? (item.itemCode ?? "")
                                : `${item.itemNumber}.${item.itemSubnumber ?? 0}`;

                              return (
                                <Table.Tr key={item.generalSidecostItemId}>
                                  {!data.hideWorkItemNumber && (
                                    <Table.Td>{itemNumberLabel}</Table.Td>
                                  )}
                                  <Table.Td>{item.name}</Table.Td>
                                  <Table.Td>{item.unitName}</Table.Td>
                                  <Table.Td>
                                    <NumberInput
                                      value={item.unitPrice ?? ""}
                                      onChange={(value) =>
                                        updateItem(
                                          group.id,
                                          item.generalSidecostItemId,
                                          "unitPrice",
                                          normalizeNumberInputValue(value),
                                        )
                                      }
                                      decimalScale={2}
                                      fixedDecimalScale={false}
                                      allowNegative={false}
                                      min={0}
                                      step={0.01}
                                      hideControls
                                      size="xs"
                                      disabled={!item.selected}
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <Checkbox
                                      checked={item.selected}
                                      onChange={(event) =>
                                        updateItem(
                                          group.id,
                                          item.generalSidecostItemId,
                                          "selected",
                                          event.currentTarget.checked,
                                        )
                                      }
                                      disabled={item.isInUse}
                                    />
                                  </Table.Td>
                                  {data.showWorkItemAreaPerPriceItem && (
                                    <Table.Td>
                                      <Checkbox
                                        checked={item.showAltValueField}
                                        onChange={(event) =>
                                          updateItem(
                                            group.id,
                                            item.generalSidecostItemId,
                                            "showAltValueField",
                                            event.currentTarget.checked,
                                          )
                                        }
                                        disabled={!item.selected}
                                      />
                                    </Table.Td>
                                  )}
                                </Table.Tr>
                              );
                            })}
                        </Fragment>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </>
        ) : (
          <Paper withBorder p="md">
            <Text c="dimmed">
              {getTranslation("TEXT_LABEL_VALITSE_URAKKA") ||
                "Select a contract to manage indirect costs."}
            </Text>
          </Paper>
        )}
      </Stack>
    </Card>
  );
}
